import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { formatDate } from 'lib/utils';
import { useCancelPlan, useKeepPlan } from 'features/subscription/hooks/use-seats';
import { PlanSeats } from 'features/subscription/utils/seat-pools';
import { CancelPlanDialog } from './cancel-plan-dialog';
import { PendingCheckoutAction } from './pending-checkout-action';

type T = (key: string, options?: Record<string, unknown> | string) => string;

/** "1 seat available" or "3 seats available". */
export const availableSeatsLabel = (t: T, count: number) =>
  count === 1
    ? t('SEAT_AVAILABLE_ONE', '1 seat available')
    : t('SEATS_AVAILABLE_COUNT', { count, defaultValue: '{{count}} seats available' });

/** "1 seat" or "3 seats". */
export const seatsLabel = (t: T, count: number) =>
  count === 1 ? t('SEAT_ONE', '1 seat') : t('SEATS_COUNT', { count, defaultValue: '{{count}} seats' });

/** Where a plan stands, for its badge: waiting to be paid, ending, full, with seats available, or not bought. */
export const planStateLabel = (t: T, entry: PlanSeats) => {
  if (!entry.pool) return t('SEATS_NOT_BOUGHT', 'Not bought');
  if (entry.awaitingPayment) return t('PAYMENT_PENDING', 'Payment pending');
  if (entry.paymentFailed) return t('PAYMENT_FAILED', 'Payment failed');
  if (entry.cancelScheduled) {
    return t('PLAN_ENDS_ON', { date: formatDate(entry.periodEndUtc, true), defaultValue: 'Ends {{date}}' });
  }
  return entry.freeThisPeriod > 0
    ? t('SEATS_FREE_COUNT', { count: entry.freeThisPeriod, defaultValue: '{{count}} free' })
    : t('SEATS_ALL_IN_USE', 'All in use');
};

/**
 * What an admin can do with a plan already bought: finish or drop an unpaid one, cancel a running
 * one at the period end or right away, or keep one whose cancellation is scheduled. `checkoutOnly`
 * keeps just the first, for the members page; cancelling a running plan lives in Settings.
 */
export const PlanActions = ({ entry, checkoutOnly = false }: { entry: PlanSeats; checkoutOnly?: boolean }) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState<'now' | 'periodEnd' | null>(null);
  const { mutateAsync: cancel, isPending: isCancelling } = useCancelPlan();
  const { mutate: keep, isPending: isKeeping } = useKeepPlan();
  const pool = entry.pool;
  if (!pool || (checkoutOnly && !entry.awaitingPayment)) return null;

  if (entry.cancelScheduled) {
    return (
      <Button
        variant="outline"
        disabled={isKeeping}
        onClick={() => keep(pool.subscription.subscriptionId)}
        className="h-10 min-w-32 whitespace-nowrap rounded-full border-grey-200 bg-true-white px-5 text-sm font-medium text-grey-950 shadow-none hover:bg-grey-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
      >
        {t('KEEP_PLAN', 'Keep plan')}
      </Button>
    );
  }

  // One button that opens a modal to continue the checkout or drop the unpaid plan.
  if (entry.awaitingPayment) {
    return <PendingCheckoutAction entry={entry} className="h-9 w-auto px-5 text-sm" />;
  }

  const date = formatDate(entry.periodEndUtc, true);
  const handleConfirm = async () => {
    await cancel({ pool, now: confirming !== 'periodEnd' }).catch(() => undefined);
    setConfirming(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-10 min-w-32 whitespace-nowrap rounded-full border-grey-950/40 bg-true-white px-5 text-sm font-medium text-error-700 shadow-none hover:bg-grey-50 hover:text-error-700 dark:border-border dark:bg-card dark:text-error-500 dark:hover:bg-muted"
          >
            {t('CANCEL_PLAN', 'Cancel plan')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[140] w-72">
          <DropdownMenuItem onSelect={() => setConfirming('periodEnd')} className="h-auto items-start py-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium">{t('CANCEL_AT_PERIOD_END', 'Cancel at period end')}</p>
              <p className="text-xs text-muted-foreground">
                {t('CANCEL_AT_PERIOD_END_HINT', { date, defaultValue: 'Keeps granting until {{date}}, then stops renewing.' })}
              </p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setConfirming('now')} className="h-auto items-start py-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-error-700 dark:text-error-500">{t('CANCEL_IMMEDIATELY', 'Cancel immediately')}</p>
              <p className="text-xs text-muted-foreground">
                {t('CANCEL_IMMEDIATELY_HINT', 'Everyone loses their seat now. Nothing is refunded.')}
              </p>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CancelPlanDialog
        entry={entry}
        open={confirming === 'periodEnd'}
        onOpenChange={(open) => !open && setConfirming(null)}
        isLoading={isCancelling}
        onConfirm={handleConfirm}
      />
      <ConfirmationModal
        open={confirming === 'now'}
        onOpenChange={(open) => !open && setConfirming(null)}
        nested
        confirmVariant="delete"
        title={t('CANCEL_PLAN_TITLE', { plan: entry.name, defaultValue: 'Cancel {{plan}}?' })}
        description={t('CANCEL_PLAN_NOW_CONFIRM', {
          plan: entry.name,
          count: entry.held,
          defaultValue: 'Cancelling {{plan}} now stops it for everyone on it: all {{count}} people lose their seats at once. Nothing is refunded.',
        })}
        confirmText={t('CANCEL_PLAN', 'Cancel plan')}
        cancelText={t('KEEP_PLAN', 'Keep plan')}
        isLoading={isCancelling}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

/** Why a plan cannot take anyone, or cannot get more seats, right now; "" when nothing stops it. */
export const planUnavailableNote = (t: T, entry: PlanSeats) => {
  if (entry.paymentFailed) {
    return t('PLAN_PAYMENT_FAILED', 'Its last payment failed. Update the card in billing settings to add seats again.');
  }
  if (!entry.awaitingPayment) return '';
  return t('PLAN_AWAITING_PAYMENT', 'Waiting for its first payment. Finish the checkout to use its seats.');
};
